import { Injectable, signal } from "@angular/core";
import { Observable, Subject, tap } from "rxjs";
import { RequestDomainEnum } from "../../../enums/request-domain.enum";
import { BreeceApiService } from "../breece-api.service";
import { IInstallationLicense, IInstallationLicenseOptions, IUpdateInstallationLicenseRequest } from "./response/installation-license.response";
import { IInstallationTreeAccess, IReplaceAccessEntry, IUserLookupResult } from "./response/installation-tree-access.response";
import { IInstallationTreeNode } from "./response/installation-tree-node.response";
import { IInstallationTransferContext } from "./response/installation-transfer-context.response";
import { ICreateCloudspaceTransferRequest, ICreateTransferRequest, INodeLocation, ITransferServer, ITransferStatus } from "./response/installation-transfer.response";
import { IInstallationTreeUser } from "./response/installation-tree-user.response";
import { IGroupDeleteCheck } from "./response/group-delete-check.response";
import { IGroupProductImport } from "./response/group-product-import.response";
import { ICopyWithinCloudspaceRequest, IMoveCheck, MoveCatalogueChoice } from "./response/move-check.response";
import { IParentCloudspace } from "./response/parent-cloudspace.response";
import { IParentInstallationCandidate } from "./response/parent-installation-candidate.response";

/**
 * A licence to be created on the licence server together with the installation (the admin
 * panel's licence window), sent instead of a key. The API maps country to the licence group.
 */
export interface INewLicenseRequest {
    isInternal: boolean;
    orderNumber: string | null;
    /** LicenseType code: 5 sub installation, 6 BreeceCloudBase. */
    licenseType: number;
    /** InstallationVendor: 0 Hybrid, 1 Hanshow, 2 Displaydata. */
    vendor: number;
    selectedRfMode: number | null;
    country: string | null;
    displayLimit: number | null;
    breeceGoLimit: number | null;
    /** Feature ids without an amount: 1 Office365, 2 Prestige. */
    addons: number[];
}

/** Body of POST /Installations - the shape CreateInstallationApiRequest binds on the API. */
export interface ICreateInstallationRequest {
    /** A key that already exists; null when `newLicense` is sent. */
    license: { breeceLicenseKey: string } | null;
    newLicense?: INewLicenseRequest | null;
    nameAndGroup: { name: string; alias: string | null; installationGroup: number; parentInstallation: number | null };
    contact: { contactPerson: string; phone: string | null; email: string | null };
    address: { address: string; postalCode: string; city: string; country: string };
    updateSystemConfiguration: boolean;
}

/**
 * The installation tree page (group admins organising groups, installations and access).
 * The Installation request domain attaches the active RefCode header, which must be a
 * group's RefCode - the API rejects installation RefCodes for these endpoints.
 *
 * `scope` overrides that header: the slide-ins can be opened from ANY page (the sidebar
 * installations menu), where the active RefCode may be an installation or another group.
 * The state service owns the value; null follows the active route as before.
 */
@Injectable({
    providedIn: 'root'
})
export class InstallationTreeApiService extends BreeceApiService {

    readonly scope = signal<string | null>(null);

    /** Fires after every successful STRUCTURE mutation (create/rename/move/delete/details) -
     *  the sidebar installations menu listens and refreshes, so changes show up immediately. */
    readonly mutated$ = new Subject<void>();

    /**
     * The RefCode header. `refCode` names a group other than the shared scope for one call - the
     * admin console reads another cloudspace's tree, or renames a cloudspace from its list, without
     * rescoping the tree the page is showing.
     */
    private scopeHeaders(refCode?: string): { key: string, value: string }[] {
        const scope = refCode ?? this.scope();
        return scope ? [{ key: 'RefCode', value: scope }] : [];
    }

    /**
     * The tree. `adminConsole`: the admin console's, which also lists switched-off installations and - for
     * BreeceSupport - what a move or copy from another server is still creating here. The group's own pages
     * show neither.
     */
    getTree(refCode?: string, adminConsole = false): Observable<IInstallationTreeNode[]> {
        return this.get<IInstallationTreeNode[]>('/InstallationTree',
            adminConsole ? [{ key: 'adminConsole', value: 'true' }] : [], RequestDomainEnum.Installation,
            'json', true, this.scopeHeaders(refCode));
    }

    /**
     * Queues a move inside the cloudspace that keeps something as a local copy, for the installation
     * mover; returns the request id to follow with getTransfer. The API works out what to keep itself;
     * `catalogue` is the user's answer to the catalogue question, when there was one.
     */
    queueMove(nodeType: 'group' | 'installation', id: number, targetGroupId: number, catalogue: MoveCatalogueChoice | null): Observable<number> {
        return this.post<number>(`/InstallationTree/${nodeType}s/${id}/move/queue`, { targetGroupId, catalogue },
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /** What promoting a group to a cloudspace of its own keeps on it (decision 36). Writes nothing. */
    checkPromote(groupId: number): Observable<IMoveCheck> {
        return this.postRead<IMoveCheck>(`/InstallationTree/groups/${groupId}/promote/check`, {},
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /** Queues a promotion for the installation mover; returns the request id to follow with getTransfer. */
    queuePromote(groupId: number): Observable<number> {
        return this.post<number>(`/InstallationTree/groups/${groupId}/promote`, {},
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /**
     * What a copy within the cloudspace keeps as its own (Cloud API docs/move-within-cloudspace.md, *Copy*):
     * an installation's - with its group copied too, when `copyGroup` - or a group's, alone. Writes nothing.
     */
    checkCopy(nodeType: 'group' | 'installation', id: number, targetGroupId: number, copyGroup: boolean): Observable<IMoveCheck> {
        return this.postRead<IMoveCheck>(`/InstallationTree/${nodeType}s/${id}/copy/check`, { targetGroupId, copyGroup },
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /**
     * Queues a copy within the cloudspace for the installation mover; returns the request id to follow with
     * getTransfer. An installation's copy carries its name, details and licence; a group's, its name.
     */
    queueCopy(nodeType: 'group' | 'installation', id: number, body: ICopyWithinCloudspaceRequest): Observable<number> {
        return this.post<number>(`/InstallationTree/${nodeType}s/${id}/copy`, body,
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /** Whether a product import runs in a group of another cloudspace, and whether it has the group catalogue on. */
    getGroupProductImport(groupId: number, refCode: string): Observable<IGroupProductImport> {
        return this.get<IGroupProductImport>(`/InstallationTree/groups/${groupId}/product-import`, [],
            RequestDomainEnum.Installation, 'json', true, this.scopeHeaders(refCode));
    }

    /** Current local/group/default sources for choices shown by the transfer form. */
    getTransferContext(installationId: number): Observable<IInstallationTransferContext> {
        return this.get<IInstallationTransferContext>(`/InstallationTree/installations/${installationId}/transfer-context`, [],
            RequestDomainEnum.Installation, 'json', true, this.scopeHeaders());
    }

    getUsers(): Observable<IInstallationTreeUser[]> {
        return this.get<IInstallationTreeUser[]>('/InstallationTree/users', [], RequestDomainEnum.Installation,
            'json', true, this.scopeHeaders());
    }

    lookupUser(email: string): Observable<IUserLookupResult> {
        return this.get<IUserLookupResult>('/InstallationTree/users/lookup',
            [{ key: 'email', value: email }], RequestDomainEnum.Installation, 'json', true, this.scopeHeaders());
    }

    getAccess(nodeType: 'group' | 'installation', id: number): Observable<IInstallationTreeAccess> {
        return this.get<IInstallationTreeAccess>(`/InstallationTree/${nodeType}s/${id}/access`, [],
            RequestDomainEnum.Installation, 'json', true, this.scopeHeaders());
    }

    createGroup(name: string, parentGroupId: number): Observable<IInstallationTreeNode> {
        return this.post<IInstallationTreeNode>('/InstallationTree/groups', { name, parentGroupId },
            RequestDomainEnum.Installation, [], this.scopeHeaders())
            .pipe(tap(() => this.mutated$.next()));
    }

    /** A cloudspace is a group: the admin list renames one by passing its own RefCode. */
    renameGroup(groupId: number, name: string, refCode?: string): Observable<IInstallationTreeNode> {
        return this.put<IInstallationTreeNode>(`/InstallationTree/groups/${groupId}`, { name },
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode))
            .pipe(tap(() => this.mutated$.next()));
    }

    /**
     * Step one of the parent picker: the cloudspaces of this SERVER that hold an installation a
     * sub-installation could hang under. Cross-server cloudspaces are a different list entirely -
     * a parent has to share the database.
     */
    getParentCloudspaces(refCode?: string): Observable<IParentCloudspace[]> {
        return this.get<IParentCloudspace[]>('/Installations/parent-cloudspaces', [],
            RequestDomainEnum.Installation, 'json', true, this.scopeHeaders(refCode));
    }

    /**
     * Step two: the installations of `cloudspaceGroupId`, or - with none given - every enabled main
     * installation on the server, each naming the cloudspace it came from so the first field can fill
     * itself in. Name / alias / RefCode search, capped at `top`.
     */
    searchParentCandidates(search: string, cloudspaceGroupId: number | null = null, top = 20, refCode?: string): Observable<IParentInstallationCandidate[]> {
        const params = [{ key: 'search', value: search }, { key: 'top', value: String(top) }];
        if (cloudspaceGroupId != null) params.push({ key: 'cloudspaceGroupId', value: String(cloudspaceGroupId) });
        return this.get<IParentInstallationCandidate[]>('/Installations/parent-candidates', params,
            RequestDomainEnum.Installation, 'json', true, this.scopeHeaders(refCode));
    }

    /** The LicenseType code behind a typed key (5 = sub installation), so the add form knows to ask
     *  for a parent. Errors are the caller's - an incomplete or unknown key is not worth a toast. */
    getLicenseType(licenseKey: string, refCode?: string): Observable<{ licenseType: number }> {
        return this.get<{ licenseType: number }>('/License/type', [{ key: 'license', value: licenseKey }],
            RequestDomainEnum.Installation, 'json', false, this.scopeHeaders(refCode));
    }

    /**
     * Creates an installation with an existing licence key, through the app's own
     * POST /Installations (licence activation, RefCode, event log, features all live there). The
     * response is not a tree node - the caller reloads the tree - so only the id comes back typed.
     * `refCode` scopes the call to the cloudspace being edited, as renameGroup does.
     */
    createInstallation(request: ICreateInstallationRequest, refCode?: string): Observable<{ installationID: number }> {
        return this.post<{ installationID: number }>('/Installations', request,
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode))
            .pipe(tap(() => this.mutated$.next()));
    }

    /**
     * The installation's licence as the licence server holds it (admin console License tab). A 404
     * means the licence server has no record it will show this cloud server; the tab shows that
     * itself, so the shared error handling is off for this call.
     */
    getInstallationLicense(installationId: number, refCode?: string): Observable<IInstallationLicense> {
        return this.get<IInstallationLicense>(`/InstallationTree/installations/${installationId}/license`, [],
            RequestDomainEnum.Installation, 'json', false, this.scopeHeaders(refCode));
    }

    /** The licence server's lists for the licence edit form: every licence group, the RF modes. */
    getLicenseOptions(refCode?: string): Observable<IInstallationLicenseOptions> {
        return this.get<IInstallationLicenseOptions>('/InstallationTree/license-options', [],
            RequestDomainEnum.Installation, 'json', true, this.scopeHeaders(refCode));
    }

    /** Applies the licence edit on the licence server and returns the licence as it is afterwards. */
    updateInstallationLicense(installationId: number, body: IUpdateInstallationLicenseRequest, refCode?: string): Observable<IInstallationLicense> {
        return this.put<IInstallationLicense>(`/InstallationTree/installations/${installationId}/license`, body,
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode));
    }

    moveGroup(groupId: number, targetGroupId: number): Observable<IInstallationTreeNode> {
        return this.post<IInstallationTreeNode>(`/InstallationTree/groups/${groupId}/move`, { targetGroupId },
            RequestDomainEnum.Installation, [], this.scopeHeaders())
            .pipe(tap(() => this.mutated$.next()));
    }

    /**
     * What would stop deleting the group: what it still owns, whether it is the current group or being moved.
     * Read-only. The shared error handling is off: the confirmation falls back to the plain delete, which the
     * API still refuses with its own message.
     */
    checkGroupDelete(groupId: number): Observable<IGroupDeleteCheck> {
        return this.get<IGroupDeleteCheck>(`/InstallationTree/groups/${groupId}/delete-check`, [],
            RequestDomainEnum.Installation, 'json', false, this.scopeHeaders());
    }

    deleteGroup(groupId: number): Observable<void> {
        return this.delete<void>(`/InstallationTree/groups/${groupId}`,
            RequestDomainEnum.Installation, [], this.scopeHeaders())
            .pipe(tap(() => this.mutated$.next()));
    }

    /** Activate / Deactivate (BreeceSupport, the admin console): switches the installation on or off. */
    setInstallationEnabled(installationId: number, isEnabled: boolean): Observable<IInstallationTreeNode> {
        return this.put<IInstallationTreeNode>(`/InstallationTree/installations/${installationId}/enabled`, { isEnabled },
            RequestDomainEnum.Installation, [], this.scopeHeaders())
            .pipe(tap(() => this.mutated$.next()));
    }

    updateInstallation(installationId: number, body: {
        name: string; alias?: string; address?: string; postalCode?: string; city?: string; country?: string;
        contactPerson?: string; phone?: string; contactEmail?: string;
    }): Observable<IInstallationTreeNode> {
        return this.put<IInstallationTreeNode>(`/InstallationTree/installations/${installationId}`, body,
            RequestDomainEnum.Installation, [], this.scopeHeaders())
            .pipe(tap(() => this.mutated$.next()));
    }

    moveInstallation(installationId: number, targetGroupId: number): Observable<IInstallationTreeNode> {
        return this.post<IInstallationTreeNode>(`/InstallationTree/installations/${installationId}/move`, { targetGroupId },
            RequestDomainEnum.Installation, [], this.scopeHeaders())
            .pipe(tap(() => this.mutated$.next()));
    }

    /** What a move inside the cloudspace changes and keeps as local copies. POST because it takes a body, but it writes nothing. */
    checkMove(nodeType: 'group' | 'installation', id: number, targetGroupId: number): Observable<IMoveCheck> {
        return this.postRead<IMoveCheck>(`/InstallationTree/${nodeType}s/${id}/move/check`, { targetGroupId },
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /**
     * Hands an installation - or a group and its whole node tree - to the installation mover, to be
     * copied into another cloudspace's database.
     *
     * <p>Queued, not done. `moveInstallation` above is a re-parent inside one database: one UPDATE,
     * finished before the response. This is minutes of row-by-row copying in another database, so
     * the API returns a request id the moment the row is written and the work carries on without
     * the browser. Poll `getTransfer`.</p>
     *
     * No `mutated$` here: nothing has changed yet when this resolves. The caller fires it when the
     * transfer actually finishes, which is when the source tree is different.
     */
    transfer(nodeType: 'group' | 'installation', id: number, body: ICreateTransferRequest, refCode?: string): Observable<number> {
        return this.post<number>(`/InstallationTree/${nodeType}s/${id}/transfer`, body,
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode));
    }

    /**
     * Every database a transfer could be sent to, and which environment each is in.
     *
     * Deliberately not the cloudspace server list: that comes from this environment's installations
     * service and so only ever shows this environment. This comes from the mover, which is
     * configured with all of them - and it is the mover's worker that keeps it current, so an old
     * `refreshedUtc` is the sign that queueing work would be queueing it for nobody.
     */
    getTransferServers(refCode?: string): Observable<ITransferServer[]> {
        return this.get<ITransferServer[]>('/InstallationTree/transfers/servers', [],
            RequestDomainEnum.Installation, 'json', false, this.scopeHeaders(refCode));
    }

    /**
     * Queues a whole cloudspace's move or copy to another server, where it lands as a cloudspace (Andreas,
     * 22 Oct 2026); returns the request id to follow with getAnyTransfer. Scoped to the cloudspace's own
     * RefCode, whatever the tree shows.
     */
    transferCloudspace(groupId: number, body: ICreateCloudspaceTransferRequest, refCode: string): Observable<number> {
        return this.post<number>(`/InstallationTree/cloudspaces/${groupId}/transfer`, body,
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode));
    }

    /**
     * One transfer of the environment's whole queue, whichever server it came from - BreeceSupport's, as
     * listAllTransfers. A cloudspace's RefCode goes with it to the new server at the handover, so
     * getTransfer, which answers for moves out of the RefCode's server, loses it there.
     */
    /**
     * Every move an installation or group took part in, by its RefCode, newest first and running ones included: the
     * History tab of the admin console's details. BreeceSupport.
     */
    getTransferHistory(refCode: string): Observable<ITransferStatus[]> {
        return this.get<ITransferStatus[]>(`/InstallationTree/transfers/all/history/${encodeURIComponent(refCode)}`, [],
            RequestDomainEnum.None, 'json', false);
    }

    /**
     * The node a RefCode names and the cloudspace it is in, on whichever server: to open it in the admin console. In the
     * path, not the RefCode header, so the API resolves it afresh - its header routing is cached, and right after a move to
     * another server would still name the old database.
     */
    locateNode(refCode: string): Observable<INodeLocation> {
        return this.get<INodeLocation>(`/InstallationTree/locate/${encodeURIComponent(refCode)}`, [], RequestDomainEnum.None, 'json', false);
    }

    getAnyTransfer(requestId: number): Observable<ITransferStatus> {
        return this.get<ITransferStatus>(`/InstallationTree/transfers/all/${requestId}`, [],
            RequestDomainEnum.None, 'json', false);
    }

    /**
     * The activity panel's moves: under way, or finished in the last week, that the place on screen
     * reaches - the active route's RefCode, not the tree's scope. Any role: seeing a move needs only
     * some access to it. The shared error handling is off: the panel polls, and keeps what it had.
     */
    listTransfers(): Observable<ITransferStatus[]> {
        return this.get<ITransferStatus[]>('/InstallationTree/transfers', [],
            RequestDomainEnum.Installation, 'json', false);
    }

    /** The same list across the environment's whole queue, for the admin console. BreeceSupport, no RefCode. */
    listAllTransfers(): Observable<ITransferStatus[]> {
        return this.get<ITransferStatus[]>('/InstallationTree/transfers/all', [],
            RequestDomainEnum.None, 'json', false);
    }

    /** How far a queued transfer has got, overall and per installation. */
    getTransfer(requestId: number): Observable<ITransferStatus> {
        return this.get<ITransferStatus>(`/InstallationTree/transfers/${requestId}`, [],
            RequestDomainEnum.Installation, 'json', false, this.scopeHeaders());
    }

    /**
     * The newest transfer of this installation or group, so the slide-in can show it again after it
     * was closed - the transfer carries on without the browser. Null (204) when there is none. The
     * shared error handling is off: without an answer the slide-in shows the form, which is no error.
     */
    getLatestTransfer(nodeType: 'group' | 'installation', id: number, refCode?: string): Observable<ITransferStatus | null> {
        return this.get<ITransferStatus | null>(`/InstallationTree/${nodeType}s/${id}/transfers/latest`, [],
            RequestDomainEnum.Installation, 'json', false, this.scopeHeaders(refCode));
    }

    /**
     * Asks for the old installation a move to another server left behind (kept, switched off) to be
     * deleted. The mover deletes it a few minutes later; asking twice is the same as asking once.
     * Nothing in the tree changes when this resolves, so no `mutated$` - poll `getTransfer`.
     */
    requestOriginalDeletion(requestId: number): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/${requestId}/delete-original`, {},
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }

    /**
     * Try again, on a transfer that did not go through: it carries on from where it stopped. The mover
     * picks this up within seconds; poll `getTransfer`.
     */
    tryTransferAgain(requestId: number, refCode?: string): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/${requestId}/try-again`, {},
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode));
    }

    /**
     * Clean up, on a transfer that did not go through: everything it left at the destination is
     * removed, over a few minutes. Afterwards it can be started again from the beginning. Poll
     * `getTransfer`.
     */
    cleanUpTransfer(requestId: number, refCode?: string): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/${requestId}/clean-up`, {},
            RequestDomainEnum.Installation, [], this.scopeHeaders(refCode));
    }

    /**
     * Try again from the admin console's activity panel, on a move from any cloud of the environment - as
     * `listAllTransfers` lists them. BreeceSupport, no RefCode; the mover locks it again.
     */
    tryAnyTransferAgain(requestId: number): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/all/${requestId}/try-again`, {}, RequestDomainEnum.None);
    }

    /**
     * Deletes what a finished move kept on its old server, from the admin console's activity panel - a move from any
     * cloud, as `tryAnyTransferAgain`. A whole cloudspace kept on its old server is found only this way: its RefCode
     * went with it. The mover deletes it a few minutes later.
     */
    deleteAnyTransferOriginal(requestId: number): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/all/${requestId}/delete-original`, {}, RequestDomainEnum.None);
    }

    /** Cancel / roll back from the admin console's activity panel, as `tryAnyTransferAgain`. */
    cleanUpAnyTransfer(requestId: number): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/all/${requestId}/clean-up`, {}, RequestDomainEnum.None);
    }

    /** Remove again what the delete service kept of a move, from the admin console's activity panel (support). */
    removeAnyTransferAgain(requestId: number): Observable<void> {
        return this.post<void>(`/InstallationTree/transfers/all/${requestId}/remove-again`, {}, RequestDomainEnum.None);
    }

    replaceAccess(nodeType: 'group' | 'installation', id: number, entries: IReplaceAccessEntry[]): Observable<IInstallationTreeAccess> {
        return this.put<IInstallationTreeAccess>(`/InstallationTree/${nodeType}s/${id}/access`, { entries },
            RequestDomainEnum.Installation, [], this.scopeHeaders());
    }
}
